/* @flow */

import React from 'react';
import Immutable from 'immutable';
import uuid from 'uuid';
import { Grid, Card, Header } from 'semantic-ui-react';
import GalleryImageSelector from './GalleryImageSelector';
import GalleryItem from './GalleryItem';
import { reorderElements, enforceElementsOrder } from 'utils/ordering';
import type { MySpaceDocument, GalleryImage } from '../types';

type Props = {
  doc: MySpaceDocument,
  onUpdate: (doc: MySpaceDocument) => void,
};

const updateImages = images => updateFunction =>
  updateFunction(Immutable.Map(images.map(i => [i.order, i])).toList());

const mergeImages = (images, updated) =>
  updateImages(images)(map =>
    map.update(updated.order, i => ({
      ...i,
      ...updated,
    }))
  );

const removeImage = (images, toDelete) =>
  updateImages(images)(map => map.remove(toDelete.order));

const GalleryContent = ({ doc, onUpdate }: Props) => {
  const update = images => {
    const getThumbnailFromImages = () => {
      if (images && images.size > 0) {
        const resource = images.get(0).resource;
        return resource ? resource.source : null;
      }
      return null;
    };

    onUpdate({
      ...doc,
      thumbnail: doc.thumbnail || getThumbnailFromImages(),
      items: images.toArray(),
    });
  };

  const imageSelected = (resource: BucketResource) => {
    const images = doc.items || [];
    const newImage = {
      id: uuid(),
      order: images.length,
      resource,
    };

    update(Immutable.List([...images, newImage]));
  };

  const imageUpdated = (updated: GalleryImage) => {
    const newImages = mergeImages(doc.items, updated);
    update(newImages);
  };

  const reorderImages = (image: MySpaceDocument, order: number) => {
    const reordered = reorderElements(doc.items, image, order);
    update(reordered);
  };

  const imageDeleted = (image: GalleryImage) => {
    const newImages = enforceElementsOrder(removeImage(doc.items, image));
    update(newImages);
  };

  return (
    <Grid.Column width={16}>
      <Header as="h4">Images</Header>
      <GalleryImageSelector onSelected={imageSelected} />
      <Card.Group itemsPerRow={4}>
        {doc.items.map(i => (
          <GalleryItem
            key={i.order}
            image={i}
            images={doc.items}
            onUpdate={imageUpdated}
            onOrderChanged={reorderImages}
            onDelete={imageDeleted}
          />
        ))}
      </Card.Group>
    </Grid.Column>
  );
};

export default GalleryContent;



// WEBPACK FOOTER //
// ./src/modules/my-space/components/GalleryContent.js